import { ScrollView, StyleSheet, View } from "react-native";
import React, { useEffect, useState } from "react";
import {
  DrawerParamList,
  RootStackParamList,
} from "../../types/navigationParams";
import { DrawerScreenProps } from "@react-navigation/drawer";
import { CompositeScreenProps } from "@react-navigation/native";
import { FAB } from "react-native-paper";
import { HorizontalBoard } from "./components/HorizontalBoard";
import { TableBoard } from "./components/TableBoard";
import { useAppSelector } from "../../redux/store";
import { update } from "../../services/firestoreServices";
import { BoardTypes, BoardTypeSelector } from "./components/BoardTypeSelector";
import FilterBar from "../../components/FilterBar";
import { useBoardData } from "./hooks/useBoardData";
import { useBoardFilters } from "./hooks/useBoardFilters";
import { useBoardSections } from "./hooks/useBoardSections";
import { serverTimestamp } from "../../firebase";
import { getStatusAt } from "../../utils/utils";
import { NativeStackScreenProps } from "@react-navigation/native-stack";

type Props = CompositeScreenProps<
  DrawerScreenProps<DrawerParamList, "BoardScreen">,
  NativeStackScreenProps<RootStackParamList, "Drawer">
>;

const INDEX_FIELD = "index";

export default function BoardScreen({ navigation, route }: Props) {
  const config = useAppSelector((state) => state.auth.config);
  const state = useAppSelector((state) => state.auth);
  const params = config.screens.find((s) => s.name === route.name)!;
  // params may come from dynamic config; read optional flags via safe access to avoid TS errors
  const {
    onlyCompany = true,
    where,
    collection: collectionName,
    showAdd,
    filters = [],
    orderBy,
    defaultPresentation = BoardTypes.horizontal,
    defaultPresentationByRole,
    tableResponsive,
    statusOptions,
    reordering,
    statusColors,
  } = params;
  const showBoardTypeSelector: boolean =
    typeof (params as any).showBoardTypeSelector === "boolean"
      ? (params as any).showBoardTypeSelector
      : true;
  const hasReordering = !!reordering?.length;
  const canReorder = !!reordering?.includes(state.user.role);
  const roleDefaultPresentation =
    defaultPresentationByRole?.[state.user.role] ?? defaultPresentation;
  const [boardPresentation, setBoardPresentation] = useState<string>(
    roleDefaultPresentation,
  );

  useEffect(() => {
    setBoardPresentation(roleDefaultPresentation);
  }, [roleDefaultPresentation]);

  const collection = config.database.find(
    (item) => item.name === collectionName,
  )!;

  const permittedFields = collection.fields.filter((item) =>
    item.board?.includes(state.user.role),
  );
  const visibleFilters = filters.filter(
    (filter) => !filter.visibleBy || filter.visibleBy.includes(state.user.role),
  );
  const canAdd = showAdd === undefined || showAdd.includes(state.user.role);
  const configSections =
    statusOptions?.map((item) => item.value) ?? config.statuses;

  const { activeFilters, handleFilterChange, applyFilters } =
    useBoardFilters(visibleFilters);
  const { filteredData } = useBoardData(
    collectionName,
    where,
    onlyCompany,
    collection.visibleAt,
    activeFilters,
    applyFilters,
  );
  const sections = useBoardSections(
    filteredData,
    configSections,
    statusOptions,
    hasReordering ? INDEX_FIELD : undefined,
  );
  // todo fix navigation issue
  const navigateToDetailsScreen = (data: Record<string, any>) => {
    navigation.navigate("DetailsScreen", {
      fields: collection.fields,
      data: data,
      collection: collection,
    });
  };

  function handleStatusChange(docId: string, newStatus: string) {
    update(collectionName, docId, {
      status: newStatus,
      statusChangedAt: serverTimestamp(),
      [getStatusAt(newStatus)]: serverTimestamp(),
    })
      .then(() => {})
      .catch((error) => {
        console.error("Failed to update document status:", error);
      });
  }

  async function handleMoveWithinColumn(
    card: Record<string, any>,
    direction: "up" | "down",
  ) {
    if (!canReorder) {
      return;
    }

    const currentSection = sections.find(
      (section) =>
        section.title === card.columnName || section.title === card.status,
    );

    if (!currentSection) {
      return;
    }

    const currentIndex = currentSection.data.findIndex(
      (item) => item.id === card.id,
    );

    if (currentIndex < 0) {
      return;
    }

    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= currentSection.data.length) {
      return;
    }

    const targetCard = currentSection.data[targetIndex];

    if (!targetCard) {
      return;
    }

    const currentIndexValue = card[INDEX_FIELD];
    const targetIndexValue = targetCard[INDEX_FIELD];

    if (currentIndexValue == null || targetIndexValue == null) {
      console.error(
        "Failed to reorder cards within board column: missing index values.",
      );
      return;
    }

    try {
      await update(collectionName, card.id, {
        [INDEX_FIELD]: targetIndexValue,
      });
      await update(collectionName, targetCard.id, {
        [INDEX_FIELD]: currentIndexValue,
      });
    } catch (error) {
      console.error("Failed to reorder cards within board column:", error);
    }
  }

  const renderContent = () => {
    switch (boardPresentation) {
      case BoardTypes.horizontal:
        return (
          <HorizontalBoard
            sections={sections}
            fields={permittedFields}
            navigateToDetails={navigateToDetailsScreen}
            onStatusChange={handleStatusChange}
            onMoveWithinColumn={canReorder ? handleMoveWithinColumn : undefined}
            statusOptions={statusOptions!}
            statusColors={statusColors}
          />
        );
      case BoardTypes.table:
        return (
          <TableBoard
            fields={permittedFields}
            sortFields={collection.fields}
            navigateToDetails={navigateToDetailsScreen}
            rawData={filteredData}
            orderBy={orderBy}
            responsiveEnabled={tableResponsive?.enabled}
            responsiveBreakpoint={tableResponsive?.breakpoint}
          />
        );
      default:
        return (
          <HorizontalBoard
            sections={sections}
            fields={permittedFields}
            navigateToDetails={navigateToDetailsScreen}
            onStatusChange={handleStatusChange}
            onMoveWithinColumn={canReorder ? handleMoveWithinColumn : undefined}
            statusOptions={statusOptions!}
            statusColors={statusColors}
          />
        );
    }
  };

  return (
    <View style={{ flex: 1, position: "relative" }}>
      <View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            flexDirection: "row",
            gap: 10,
            alignItems: "center",
            padding: 16,
            paddingBottom: 0,
            paddingTop: 0,
          }}
        >
          {/* Render BoardTypeSelector only when enabled in screen config */}
          {showBoardTypeSelector && (
            <BoardTypeSelector
              boardPresentation={boardPresentation}
              setBoardPresentation={(value) => setBoardPresentation(value)}
            />
          )}
          {visibleFilters.length > 0 && (
            <FilterBar
              filters={visibleFilters}
              onFilterChange={handleFilterChange}
              activeFilters={activeFilters}
            />
          )}
        </ScrollView>
      </View>
      {renderContent()}
      {canAdd && (
        <FAB
          icon="plus"
          style={styles.fab}
          onPress={() =>
            navigation.navigate("FormScreen", {
              item: undefined,
              collection: collection.name,
            })
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    bottom: 30,
  },
});
